import { Body, Button, Icon, Tooltip } from '@assembly-js/design-system';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useChannelStateContext,
  useMessageContext,
  useMessageListContext,
} from 'stream-chat-react';

import { RouteContext } from 'src/context';
import { isOnCrmPage } from 'src/features/assistant/action-item/utils';
import {
  widgetButtonStyles,
  WidgetContainer,
  WidgetContent,
  WidgetDraft,
  WidgetHeader,
} from 'src/features/assistant/action-item/Widget';
import { useAIAssistantStatus } from 'src/features/assistant/useAIAssistantStatus';
import { useAppDispatch } from 'src/hooks/useStore';
import { AssistantUserID } from 'src/store/clients/types';
import { alertSnackbar } from 'src/store/ui/actions';
import { CTA } from 'src/types/stream-chat';
import { addCustomCopyBehavior } from 'src/utils/ClipboardUtils';

const suggestedDraftActions = ['shorten', 'lengthen', 'simplify'] as const;

const convertToBlockquote = (text: string) => {
  const lines = text.split('\n');
  const blockquoteLines = lines.map((line) =>
    line.trim() === '' ? '>' : '> ' + line,
  );
  return blockquoteLines.join('\n');
};

export const MessageDraft = ({
  cta,
  handleCTAClick,
}: {
  cta: CTA;
  handleCTAClick: (cta: CTA) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { channel, messages } = useChannelStateContext();
  const { message } = useMessageContext();
  const { scrollToBottom } = useMessageListContext();
  const { status: aiAssistantStatus } = useAIAssistantStatus();
  const { pathname } = useContext(RouteContext);
  const onCrmPage = isOnCrmPage(pathname);

  // Assistant is busy if it has an active status (not null, error, or canceled)
  const isAssistantBusy = !!(
    aiAssistantStatus &&
    aiAssistantStatus.status !== 'error' &&
    aiAssistantStatus.status !== 'canceled'
  );

  if (!cta.message_data?.body || typeof cta.message_data.body !== 'string') {
    console.error('MessageDraft did not receive a valid message body');
    return null;
  }

  const messageBody = cta.message_data.body;

  return (
    <>
      <WidgetContainer className="mb-5">
        <WidgetHeader
          widgetName="MESSAGE DRAFT"
          buttonName="Insert message"
          buttonIcon="Insert"
          buttonDisabled={!onCrmPage}
          onClick={() => handleCTAClick(cta)}
        />
        {onCrmPage ? (
          <WidgetContent>
            <WidgetDraft>
              <Body>{messageBody}</Body>
            </WidgetDraft>
          </WidgetContent>
        ) : (
          <div className="w-full px-8 py-6 flex flex-col items-center gap-1">
            <Icon icon="Warning" className="w-5 h-5 text-warning-primary" />
            <Body className="text-center">
              {t(($) => $.assistant.widgets.message_draft.page_error)}
            </Body>
          </div>
        )}
      </WidgetContainer>
      {onCrmPage && (
        <div
          className="flex flex-items flex-wrap gap-2 select-none"
          {...addCustomCopyBehavior({ excludeFromCopy: true })}
        >
          {suggestedDraftActions.map((action) => {
            const button = (
              <Button
                key={action}
                className={widgetButtonStyles}
                variant="secondary"
                disabled={isAssistantBusy}
                label={t(
                  ($) =>
                    $.assistant.widgets.message_draft.suggested_actions[action]
                      .label,
                )}
                onClick={() => {
                  void (async () => {
                    try {
                      const suggestionMessage = t(
                        ($) =>
                          $.assistant.widgets.message_draft.suggested_actions[
                            action
                          ].message,
                      );

                      const assistantMessages = (messages ?? []).filter(
                        (msg) => msg.user?.id === AssistantUserID,
                      );

                      if (
                        message.id ===
                        assistantMessages[assistantMessages.length - 1]?.id
                      ) {
                        // this widget is in the most recent assistant message
                        await channel.sendMessage({
                          text: suggestionMessage + '.',
                          mentioned_users: [AssistantUserID],
                        });
                      } else {
                        await channel.sendMessage({
                          text:
                            suggestionMessage +
                            ':\n' +
                            convertToBlockquote(messageBody),

                          mentioned_users: [AssistantUserID],
                        });
                      }

                      scrollToBottom();
                    } catch (err) {
                      console.error('Failed to send message:', err);
                      dispatch(
                        alertSnackbar({
                          errorMessage:
                            'There was a problem with updating this draft, please try again.',
                        }),
                      );
                    }
                  })();
                }}
              />
            );

            // Wrap in tooltip when assistant is busy
            if (isAssistantBusy) {
              return (
                <Tooltip
                  key={action}
                  content={
                    <Body size="xs">
                      The Assembly Assistant is currently busy, please wait or
                      cancel.
                    </Body>
                  }
                  position="top"
                  triggerClassname="cursor-not-allowed"
                >
                  <span>{button}</span>
                </Tooltip>
              );
            }

            return button;
          })}
        </div>
      )}
    </>
  );
};
